import {useState, useMemo, useCallback} from 'react';
import {
  Table,
  Pagination,
  Button,
  Form,
  Spin,
  Select,
  DatePicker,
  Input,
  Dropdown,
  Tag,
} from 'antd';
import {useQuery, useMutation, useLazyQuery} from '@apollo/client';
import {
  GET_ALL_REFERRAL_PARTNERS,
  GET_REFERRAL_PARTNER_BY_ID,
  CREATE_REFERRAL_PARTNER,
  UPDATE_REFERRAL_PARTNER,
  REFERRAL_PARTNER_STATS,
} from './resolvers';
import {toast} from 'react-toastify';
import moment from 'moment';
import {currencySymbols} from 'utils/mock';
import DBModal from 'components/modal/modal';
import MetricCard from 'components/metricCard/MetricCard';
import {ReferralPartnersForm} from './ReferralPartnersForm';
import {
  RevokePartnerContent,
  ResetPartnerPasswordContent,
  ExportPartnersContent,
} from './PartnerActionModals';
import {
  referralPartnerTypeFilters,
  buildAcademyCommissionInput,
  validateAcademyCommission,
  academyCommissionToFormValues,
} from './utils';
import styles from './referralPartners.module.scss';
import {useNavigate} from 'react-router-dom';
import {useReferralPartnerStore} from '../../zustand/store';
import {debounce, sanitizeInput} from 'utils/helper';
import dotsIcon from 'images/dots.svg';

const {RangePicker} = DatePicker;

const initialsOf = record =>
  `${record?.firstName?.[0] || ''}${
    record?.lastName?.[0] || ''
  }`.toUpperCase() ||
  record?.email?.[0]?.toUpperCase() ||
  '?';

/**
 * The read APIs convert before they total, and report what they converted into.
 * Defaulting to XOF labelled USD figures as CFA — the label contradicted the
 * number. The currency is passed in, and an amount with none renders bare
 * rather than being attributed to a currency nobody chose.
 */
const PARTNER_LIST_CURRENCY = 'USD';

const formatMoney = (amount, currency) => {
  const value = Number(amount || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (!currency) return value;
  const symbol = currencySymbols?.[currency]?.symbol || `${currency} `;
  return `${symbol}${value}`;
};

export const ReferralPartners = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [editModal, setEditModal] = useState({open: false, partner: null});
  const [revokeModal, setRevokeModal] = useState({open: false, partner: null});
  const [passwordModal, setPasswordModal] = useState({
    open: false,
    partner: null,
  });
  const [exportModal, setExportModal] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({});
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const navigate = useNavigate();

  const activeFilters = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(filters).filter(
          ([, value]) => value !== undefined && value !== null && value !== '',
        ),
      ),
    [filters],
  );

  const {data, loading, refetch} = useQuery(GET_ALL_REFERRAL_PARTNERS, {
    variables: {
      page,
      limit,
      ...(Object.keys(activeFilters).length ? {filters: activeFilters} : {}),
    },
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
    notifyOnNetworkStatusChange: true,
  });

  const {data: statsData, loading: statsLoading} = useQuery(
    REFERRAL_PARTNER_STATS,
    {
      variables: Object.keys(activeFilters).length
        ? {filters: activeFilters}
        : {},
      errorPolicy: 'all',
      onError: error => {
        // Silently handle error - stats are optional
        console.error('Error fetching referral partner stats:', error);
      },
    },
  );

  const [createPartner, {loading: createLoading}] = useMutation(
    CREATE_REFERRAL_PARTNER,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const [updatePartner, {loading: updateLoading}] = useMutation(
    UPDATE_REFERRAL_PARTNER,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  // `errorPolicy: 'all'` because this query partially fails today: the gateway
  // reports a DOWNSTREAM_SERVICE_ERROR resolving `partner.phoneNumber` while
  // still returning every other field. A blanket `onError` toast here made that
  // look like a total failure — "Unexpected error." over a form that had in
  // fact loaded. The caller decides instead, and only complains when nothing
  // usable came back.
  const [fetchPartner, {loading: loadingPartner}] = useLazyQuery(
    GET_REFERRAL_PARTNER_BY_ID,
    {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  );

  const partners = data?.adminGetReferralPartners?.partners || [];
  const metrics =
    statsData?.adminGetReferralMetrics?.__typename === 'AdminReferralMetrics'
      ? statsData.adminGetReferralMetrics
      : null;
  // `totalDocs` is the row count; `offset` is a cursor and must not be used here.
  const totalPartners =
    data?.adminGetReferralPartners?.totalDocs ??
    data?.adminGetReferralPartners?.offset ??
    0;

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const handleLimitChange = newLimit => {
    setLimit(newLimit);
    setPage(1);
  };

  const applyFilter = (key, value) => {
    setPage(1);
    setFilters(prev => ({...prev, [key]: value}));
  };

  const handleSearch = useCallback(
    debounce(value => {
      setPage(1);
      setFilters(prev => ({
        ...prev,
        search: value ? sanitizeInput(value) : undefined,
      }));
    }),
    [],
  );

  const hasFilters = Object.keys(activeFilters).length > 0;

  const filterSummary = useMemo(() => {
    const parts = [];
    if (activeFilters.search) parts.push(`search "${activeFilters.search}"`);
    if (activeFilters.referralPartnerType) {
      parts.push(`type ${activeFilters.referralPartnerType}`);
    }
    if (activeFilters.dateFrom) {
      parts.push(
        `from ${moment(activeFilters.dateFrom).format('MMM D, YYYY')}`,
      );
    }
    if (activeFilters.dateTo) {
      parts.push(`to ${moment(activeFilters.dateTo).format('MMM D, YYYY')}`);
    }
    return parts.length
      ? `Exporting partners matching: ${parts.join(', ')}.`
      : null;
  }, [activeFilters]);

  const buildPartnerInput = values => {
    const academyCommission = buildAcademyCommissionInput(values);
    return {
      input: {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        countryCode: values.countryCode,
        referralCode: values.referralCode,
        referralPartnerType: values.referralPartnerType,
        ...(values.spreadSheetId && {spreadSheetId: values.spreadSheetId}),
        // One rate per currency, not a single Float. Rows without a currency
        // are dropped rather than sent, since the gateway rejects the whole
        // input for them.
        ...(Array.isArray(values.referralPartnerCommissionOnNewInvestedUsers) &&
          values.referralPartnerCommissionOnNewInvestedUsers.length > 0 && {
            referralPartnerCommissionOnNewInvestedUsers:
              values.referralPartnerCommissionOnNewInvestedUsers
                .filter(entry => entry?.currency)
                .map(entry => ({
                  currency: entry.currency,
                  amount: Number(entry.amount || 0),
                })),
          }),
        ...(values.referralPartnerPercentage &&
          values.referralPartnerPercentage.length > 0 && {
            referralPartnerPercentage: values.referralPartnerPercentage
              // A stray empty row would fail the whole mutation on `String!`.
              .filter(percentage => percentage?.currency)
              .map(percentage => ({
                volumeThreshold: Number(percentage.volumeThreshold || 0),
                percentageCutOnFees: Number(
                  percentage.percentageCutOnFees || 0,
                ),
                // No fallback: a tier whose currency matches no tracker earns
                // nothing at all, so guessing XOF loses the partner money
                // silently. The form requires one.
                currency: percentage.currency,
              })),
          }),
        ...(academyCommission && {academyCommission}),
      },
      academyCommission,
    };
  };

  const handleCreatePartner = values => {
    const {input, academyCommission} = buildPartnerInput(values);
    const academyError = validateAcademyCommission(academyCommission);
    if (academyError) return toast.error(academyError);

    createPartner({
      variables: {
        data: {...input, password: values.password},
      },
    })
      .then(({data}) => {
        if (data?.adminCreateReferralPartner?.__typename === 'Error') {
          return toast.error(data.adminCreateReferralPartner.message);
        } else {
          toast.success(
            data?.adminCreateReferralPartner?.message ||
              'Referral partner created successfully',
          );
          form.resetFields();
          setOpenCreateModal(false);
          refetch();
        }
      })
      .catch(error => {
        toast.error(error.message || 'Something went wrong');
      });
  };

  const handleUpdatePartner = values => {
    const {input, academyCommission} = buildPartnerInput(values);
    const academyError = validateAcademyCommission(academyCommission);
    if (academyError) return toast.error(academyError);

    updatePartner({
      variables: {
        data: {
          ...input,
          partnerId: editModal.partner?.id,
          // Only sent when the admin actually touched the toggle, so an
          // unrelated edit never silently unlocks a locked portal account.
          ...(values.isAccountLocked !== undefined && {
            isAccountLocked: Boolean(values.isAccountLocked),
          }),
        },
      },
    })
      .then(({data}) => {
        if (data?.adminUpdateReferralPartner?.__typename === 'Error') {
          return toast.error(data.adminUpdateReferralPartner.message);
        }
        toast.success(
          data?.adminUpdateReferralPartner?.message ||
            'Referral partner updated successfully',
        );
        editForm.resetFields();
        setEditModal({open: false, partner: null});
        refetch();
      })
      .catch(error => {
        toast.error(error.message || 'Something went wrong');
      });
  };

  const goToPartner = record => {
    const fullName =
      `${record?.firstName || ''} ${record?.lastName || ''}`.trim() ||
      'Unknown';
    useReferralPartnerStore
      .getState()
      .setPartnerInfo(fullName, record?.email || '');
    navigate(`/dashboard/referral-partners/${record.id}`);
  };

  const openEditModal = async record => {
    setEditModal({open: true, partner: record});
    const {data: detail} = await fetchPartner({
      variables: {partnerId: record.id},
    });
    const response = detail?.adminGetReferralPartnerById;
    if (response?.__typename === 'Error') {
      toast.error(response.message);
      return;
    }
    const partner = response?.partner;
    if (!partner) {
      toast.error('Could not load this partner.');
      return;
    }

    setEditModal({open: true, partner});
    editForm.setFieldsValue({
      email: partner.email,
      firstName: partner.firstName,
      lastName: partner.lastName,
      phoneNumber: partner.phoneNumber,
      countryCode: partner.countryCode,
      referralCode: partner.referralCode,
      referralPartnerType: partner.referralPartnerType,
      spreadSheetId: partner.referralPartnerSpreadSheetId,
      // The list comes back holding a null entry for partners migrated from the
      // old scalar field. Dropping them keeps the editor from rendering a row
      // bound to nothing, which would then fail validation on save.
      referralPartnerCommissionOnNewInvestedUsers: (
        partner.referralPartnerCommissionOnNewInvestedUsers || []
      )
        .filter(entry => entry?.currency)
        .map(entry => ({currency: entry.currency, amount: entry.amount})),
      referralPartnerPercentage: (partner.referralPartnerPercentage || []).map(
        entry => ({
          volumeThreshold: entry?.volumeThreshold,
          percentageCutOnFees: entry?.percentageCutOnFees,
          currency: entry?.currency,
        }),
      ),
      ...academyCommissionToFormValues(
        partner.referralPartnerAcademyCommission,
      ),
    });
  };

  const columns = useMemo(
    () => [
      {
        title: 'Partner',
        key: 'partner',
        fixed: 'left',
        width: 280,
        render: (_, record) => (
          <div className={styles.identity}>
            <span className={styles.avatar}>{initialsOf(record)}</span>
            <span className={styles.identityText}>
              <span className={styles.nameCell}>
                {`${record?.firstName || ''} ${
                  record?.lastName || ''
                }`.trim() || 'Unnamed partner'}
              </span>
              <span className={styles.emailCell}>{record?.email || '-'}</span>
            </span>
          </div>
        ),
      },
      {
        title: 'Referral Code',
        key: 'referralCode',
        dataIndex: 'referralCode',
        width: 150,
        render: code =>
          code ? (
            <span className={styles.referralCode}>{code}</span>
          ) : (
            <span className={styles.zero}>—</span>
          ),
      },
      {
        title: 'Type',
        key: 'referralPartnerType',
        dataIndex: 'referralPartnerType',
        width: 140,
        render: type =>
          type ? (
            <Tag
              className={styles.typeTag}
              color={type === 'NONE' ? 'default' : 'purple'}>
              {type.replace(/_/g, ' ').toLowerCase()}
            </Tag>
          ) : (
            <span className={styles.zero}>—</span>
          ),
      },
      {
        title: 'Earnings',
        key: 'totalEarnings',
        dataIndex: 'totalEarnings',
        width: 150,
        align: 'right',
        render: (earnings, record) => (
          <span
            className={`${styles.money} ${
              !Number(earnings) ? styles.zero : ''
            }`}>
            {formatMoney(earnings, record?.currency || PARTNER_LIST_CURRENCY)}
          </span>
        ),
      },
      {
        title: 'Volume',
        key: 'totalVolume',
        dataIndex: 'totalVolume',
        width: 160,
        align: 'right',
        render: (volume, record) => (
          <span
            className={`${styles.money} ${!Number(volume) ? styles.zero : ''}`}>
            {formatMoney(volume, record?.currency || PARTNER_LIST_CURRENCY)}
          </span>
        ),
      },
      {
        title: 'Referees',
        key: 'totalReferees',
        dataIndex: 'totalReferees',
        width: 110,
        align: 'right',
        render: count => (
          <span className={`${styles.count} ${!count ? styles.zero : ''}`}>
            {Number(count || 0).toLocaleString('en-US')}
          </span>
        ),
      },
      {
        // `activeReferees` now counts investment activity only.
        title: 'Invested',
        key: 'activeReferees',
        dataIndex: 'activeReferees',
        width: 110,
        align: 'right',
        render: count => (
          <span className={`${styles.count} ${!count ? styles.zero : ''}`}>
            {Number(count || 0).toLocaleString('en-US')}
          </span>
        ),
      },
      {
        title: 'Academy Sales',
        key: 'totalAcademySales',
        dataIndex: 'totalAcademySales',
        width: 140,
        align: 'right',
        render: count => (
          <span className={`${styles.count} ${!count ? styles.zero : ''}`}>
            {Number(count || 0).toLocaleString('en-US')}
          </span>
        ),
      },
      {
        title: 'Created',
        key: 'createdAt',
        dataIndex: 'createdAt',
        width: 140,
        render: date =>
          date ? (
            <span className={styles.date}>
              {moment(date).format('DD MMM YYYY')}
              <span className={styles.dateTime}>
                {moment(date).format('h:mm a')}
              </span>
            </span>
          ) : (
            <span className={styles.zero}>—</span>
          ),
      },
      {
        title: '',
        key: 'actions',
        fixed: 'right',
        width: 64,
        render: (_, record) => (
          // Stop the click bubbling into the row's navigate handler.
          <div
            className={styles.actionsCell}
            onClick={event => event.stopPropagation()}>
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              menu={{
                items: [
                  {
                    key: 'view',
                    label: 'View Dashboard',
                    onClick: () => goToPartner(record),
                  },
                  {
                    key: 'edit',
                    label: 'Edit Partner',
                    onClick: () => openEditModal(record),
                  },
                  {
                    key: 'password',
                    label: 'Reset Portal Password',
                    onClick: () =>
                      setPasswordModal({open: true, partner: record}),
                  },
                  {type: 'divider'},
                  {
                    key: 'revoke',
                    label: 'Revoke Partner',
                    danger: true,
                    onClick: () =>
                      setRevokeModal({open: true, partner: record}),
                  },
                ],
              }}>
              <span className={styles.actionsTrigger}>
                <img src={dotsIcon} alt="actions" />
              </span>
            </Dropdown>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className={styles.referralPartners}>
      <div className={styles.header}>
        <div className={styles.titleWrap}>
          <h1 className={styles.title}>Referral Partners</h1>
          <p className={styles.subtitle}>
            Manage partners, their commission setup and their Academy sales.
          </p>
        </div>
        <div className={styles.headerActions}>
          <Button onClick={() => setExportModal(true)}>Export</Button>
          <Button type="primary" onClick={() => setOpenCreateModal(true)}>
            Create Partner
          </Button>
        </div>
      </div>

      <div className={styles.filterBar}>
        <Input
          allowClear
          className={styles.searchField}
          placeholder="Search by name, email or code"
          value={searchInput}
          onChange={event => {
            setSearchInput(event.target.value);
            handleSearch(event.target.value);
          }}
        />
        <Select
          allowClear
          className={styles.typeField}
          placeholder="Partner type"
          value={filters.referralPartnerType}
          onChange={value => applyFilter('referralPartnerType', value)}>
          {referralPartnerTypeFilters.map(type => (
            <Select.Option key={type.value} value={type.value}>
              {type.label}
            </Select.Option>
          ))}
        </Select>
        <RangePicker
          onChange={dates => {
            setPage(1);
            setFilters(prev => ({
              ...prev,
              dateFrom: dates?.[0]
                ? dates[0].startOf('day').format()
                : undefined,
              dateTo: dates?.[1] ? dates[1].endOf('day').format() : undefined,
            }));
          }}
        />
        {hasFilters && (
          <Button
            type="link"
            className={styles.clearButton}
            onClick={() => {
              setFilters({});
              setSearchInput('');
              setPage(1);
            }}>
            Clear filters
          </Button>
        )}
        <span className={styles.filterSpacer} />
        <span className={styles.resultCount}>
          {loading
            ? 'Loading…'
            : `${totalPartners.toLocaleString('en-US')} partner${
                totalPartners === 1 ? '' : 's'
              }${hasFilters ? ' matching filters' : ''}`}
        </span>
      </div>

      <div className={styles.statsContainer}>
        <div className={styles.statsCards}>
          <MetricCard
            label="Total Partners"
            loading={statsLoading}
            value={Number(metrics?.totalPartners || 0).toLocaleString('en-US')}
          />
          <MetricCard
            label="Total Referees"
            loading={statsLoading}
            value={Number(metrics?.totalReferees || 0).toLocaleString('en-US')}
          />
          <MetricCard
            label="Total Earnings"
            loading={statsLoading}
            value={formatMoney(metrics?.totalEarnings, metrics?.currency)}
          />
          <MetricCard
            label="Total Volume"
            loading={statsLoading}
            value={formatMoney(metrics?.totalVolume, metrics?.currency)}
          />
          <MetricCard
            label="Conversion Rate"
            loading={statsLoading}
            hint="Share of referees who went on to invest."
            value={`${Number(metrics?.conversionRate || 0).toFixed(2)}%`}
          />
          <MetricCard
            label="Avg / Partner"
            loading={statsLoading}
            hint="Average earnings per referral partner."
            value={formatMoney(
              metrics?.avgEarningsPerPartner,
              metrics?.currency,
            )}
          />
        </div>
      </div>

      <div className={styles.tableContainer}>
        <Table
          rowKey="id"
          columns={columns}
          loading={loading}
          scroll={{x: 'max-content'}}
          onRow={record => ({
            className: styles.tableRow,
            onClick: () => goToPartner(record),
          })}
          dataSource={partners}
          pagination={false}
          locale={{
            emptyText: (
              <div className={styles.emptyState}>
                <h3>
                  {hasFilters
                    ? 'No partners match those filters'
                    : 'No partners yet'}
                </h3>
                <p>
                  {hasFilters
                    ? 'Try clearing the search or widening the date range.'
                    : 'Create your first referral partner to get started.'}
                </p>
              </div>
            ),
          }}
        />
        {totalPartners > 0 && (
          <Pagination
            current={page}
            onChange={handlePageChange}
            total={totalPartners}
            showQuickJumper
            className={styles.pagination}
            showSizeChanger
            pageSize={limit}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} partners`
            }
            onShowSizeChange={(_, size) => handleLimitChange(size)}
          />
        )}
      </div>
      <DBModal
        isOpen={openCreateModal}
        handleClose={() => {
          setOpenCreateModal(false);
          form.resetFields();
        }}
        width={860}
        content={
          <ReferralPartnersForm
            form={form}
            onSubmit={handleCreatePartner}
            onCancel={() => {
              setOpenCreateModal(false);
              form.resetFields();
            }}
            loading={createLoading}
          />
        }
      />
      <DBModal
        isOpen={editModal.open}
        handleClose={() => {
          setEditModal({open: false, partner: null});
          editForm.resetFields();
        }}
        width={860}
        destroyOnClose
        content={
          loadingPartner ? (
            <div style={{textAlign: 'center', padding: 60}}>
              <Spin size="large" />
            </div>
          ) : (
            <ReferralPartnersForm
              isEdit
              form={editForm}
              onSubmit={handleUpdatePartner}
              onCancel={() => {
                setEditModal({open: false, partner: null});
                editForm.resetFields();
              }}
              loading={updateLoading}
            />
          )
        }
      />
      <DBModal
        isOpen={revokeModal.open}
        handleClose={() => setRevokeModal({open: false, partner: null})}
        width={560}
        destroyOnClose
        content={
          <RevokePartnerContent
            partner={revokeModal.partner}
            onDone={refetch}
            onClose={() => setRevokeModal({open: false, partner: null})}
          />
        }
      />
      <DBModal
        isOpen={passwordModal.open}
        handleClose={() => setPasswordModal({open: false, partner: null})}
        width={520}
        destroyOnClose
        content={
          <ResetPartnerPasswordContent
            partner={passwordModal.partner}
            onClose={() => setPasswordModal({open: false, partner: null})}
          />
        }
      />
      <DBModal
        isOpen={exportModal}
        handleClose={() => setExportModal(false)}
        width={560}
        destroyOnClose
        content={
          <ExportPartnersContent
            filters={activeFilters}
            filterSummary={filterSummary}
            onClose={() => setExportModal(false)}
          />
        }
      />
    </div>
  );
};
